import {
  DatePicker,
  Field,
  Input,
  RadioGroup,
  Select,
  Spin,
} from '@/components/commons';
import { Checkbox as AntCheckbox } from 'antd';
import { CategoryService } from '@/services/category';
import { ENDPOINT } from '@/services/endpoint';
import { EventService } from '@/services/event';
import {
  FALLBACK_URL,
  FILE_SIZE_LIMIT,
  SUPPORTED_EXTENSION,
  SUPPORTED_FORMATS,
} from '@/services/image';
import { OfferService } from '@/services/offer';
import { TagService } from '@/services/tag';
import { Status, statusOptions } from '@/utils';
import { CloseOutlined } from '@ant-design/icons';
import { Badge, Button, Form, Image, Upload } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import moment, { Moment } from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useQuery } from 'react-query';
import { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { ContentImage } from '../../ArticleList';
import { IGetDetailArticleResponse } from '@/types/response';
import { TagType } from '@/types/response/tag';
import { FORM_NAME } from './constants';

const FontAttributor: any = Quill.import('formats/font');
FontAttributor.whitelist = ['Noto Sans JP', 'Arial', 'Times New Roman'];
Quill.register(FontAttributor, true);

const AlignStyle: any = Quill.import('attributors/style/align');
Quill.register(AlignStyle, true);

interface IProps {
  isLoading: boolean;
  article?: IGetDetailArticleResponse;
}

export const ArticleForm: React.FC<IProps> = ({ isLoading, article }) => {
  const { trigger, watch, setValue, setError, clearErrors } = useFormContext();
  const formName = watch('formName');

  const { data: categoriesRes } = useQuery([ENDPOINT.CATEGORY.GET_ALL], () =>
    CategoryService.getAll(),
  );

  const { data: tagsRes } = useQuery(
    [ENDPOINT.TAG.GET_LIST, TagType.ARTICLE],
    () => TagService.getList(TagType.ARTICLE),
  );

  const [offerOptions, setOfferOptions] = useState<any[]>([]);
  const [offerPage, setOfferPage] = useState<number>(1);
  const [hasMoreOffers, setHasMoreOffers] = useState<boolean>(true);
  const [loadingOffers, setLoadingOffers] = useState<boolean>(false);
  const OFFER_LIMIT = 20;
  const [eventOptions, setEventOptions] = useState<any[]>([]);
  const [eventPage, setEventPage] = useState<number>(1);
  const [hasMoreEvents, setHasMoreEvents] = useState<boolean>(true);
  const [loadingEvents, setLoadingEvents] = useState<boolean>(false);
  const EVENT_LIMIT = 20;

  useEffect(() => {
    let canceled = false;
    const fetchEvents = async () => {
      setLoadingEvents(true);
      try {
        const res = await EventService.getForSelect({
          page: eventPage,
          limit: EVENT_LIMIT,
        } as any);
        const list = (res?.data?.data || []) as any[];
        const total = res?.data?.pagination?.total || 0;
        const mapped = list.map((e: any) => ({
          value: Number(e?.id ?? e?.value),
          label: String(e?.name ?? e?.label ?? ''),
        }));
        setEventOptions((prev) => {
          const map = new Map<number | string, string>();
          prev.forEach((o: any) => map.set(o.value, o.label));
          mapped.forEach((o) => map.set(o.value, o.label));
          return Array.from(map.entries()).map(([value, label]) => ({
            value,
            label,
          }));
        });
        setHasMoreEvents(eventPage * EVENT_LIMIT < total);
      } finally {
        if (!canceled) setLoadingEvents(false);
      }
    };
    fetchEvents();
    return () => {
      canceled = true;
    };
  }, [eventPage]);

  useEffect(() => {
    let canceled = false;
    const fetchOffers = async () => {
      setLoadingOffers(true);
      try {
        const res = await OfferService.getList({
          page: offerPage,
          limit: OFFER_LIMIT,
          status: 1,
        } as any);
        const list = (res?.data?.data || []) as any[];
        const total = res?.data?.pagination?.total || 0;
        const mapped = list.map((o: any) => ({
          value: Number(o?.id ?? o?.value),
          label: String(o?.title ?? o?.label ?? ''),
        }));
        setOfferOptions((prev) => {
          const map = new Map<number | string, string>();
          prev.forEach((opt: any) => map.set(opt.value, opt.label));
          mapped.forEach((opt) => map.set(opt.value, opt.label));
          return Array.from(map.entries()).map(([value, label]) => ({
            value,
            label,
          }));
        });
        setHasMoreOffers(offerPage * OFFER_LIMIT < total);
      } finally {
        if (!canceled) setLoadingOffers(false);
      }
    };
    fetchOffers();
    return () => {
      canceled = true;
    };
  }, [offerPage]);

  const categoryOptions = (categoriesRes?.data || []).map((c: any) => ({
    value: Number(c?.id ?? c?.value),
    label: String(c?.name ?? c?.label ?? ''),
  }));

  const tagOptions = (tagsRes?.data || []).map((t: any) => ({
    value: String(t?.name ?? t?.label ?? t),
    label: String(t?.name ?? t?.label ?? t),
  }));

  const disabledDate = (current: Dayjs) => {
    return current.isBefore(dayjs().startOf('day'));
  };

  const disabledTime = (current: Dayjs) => {
    if (!current) return {};

    const now = dayjs();
    const selectedDate = dayjs(current);

    if (selectedDate.isBefore(now, 'day')) {
      return {
        disabledHours: () => Array.from({ length: 24 }, (_, i) => i),
        disabledMinutes: () => Array.from({ length: 60 }, (_, i) => i),
      };
    }

    if (selectedDate.isSame(now, 'day')) {
      const disabledHours: number[] = [];
      for (let i = 0; i < now.hour(); i++) {
        disabledHours.push(i);
      }

      const disabledMinutes = (selectedHour: number) => {
        if (selectedHour === now.hour()) {
          const minutes = [];
          for (let i = 0; i <= now.minute(); i++) {
            minutes.push(i);
          }
          return minutes;
        }
        return [];
      };

      return {
        disabledHours: () => disabledHours,
        disabledMinutes: disabledMinutes,
      };
    }

    return {
      disabledHours: () => [],
      disabledMinutes: () => [],
    };
  };

  const coverImage: ContentImage | null = watch('coverImage');
  const isDisabled = watch('isDisabled');
  const publicAt = watch('publicAt');
  const status = watch('status');

  const beforeUpload = (file: File) => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (file.size > FILE_SIZE_LIMIT) {
      setError('attachment', {
        type: 'validate',
        message: '画像のサイズは10MBを超えています。',
      });
      return Upload.LIST_IGNORE;
    }

    if (
      !SUPPORTED_EXTENSION.includes(fileExtension || '') ||
      !SUPPORTED_FORMATS.includes(file.type)
    ) {
      setError('attachment', {
        type: 'validate',
        message: '画像は .jpg、.png、.jpeg 形式ではありません。',
      });
      return Upload.LIST_IGNORE;
    }
    clearErrors('attachment');
    handleUpload(file);

    return true;
  };

  const handleUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      setValue('coverImage', {
        previewUrl: reader.result as string,
        file,
      });
    };
    reader.readAsDataURL(file);
  };

  const isPublic = useMemo(
    () =>
      !!article &&
      article.status === Status.PUBLIC &&
      moment(article.publicAt).isSameOrBefore(moment()),
    [article],
  );

  useEffect(() => {
    if (isPublic && status === Status.PUBLIC && article?.publicAt) {
      setValue('publicAt', moment(article.publicAt));
    }
  }, [status, isPublic, article, setValue]);

  return (
    <Form layout='vertical'>
      <Spin spinning={isLoading}>
        <Field label='タイトル' name='title' required>
          <Input placeholder='タイトル' maxLength={100} disabled={isDisabled} />
        </Field>

        <div className='mb-4'>
          <AntCheckbox
            checked={watch('isCompanyType') ?? false}
            onChange={(e) => setValue('isCompanyType', e.target.checked)}
            disabled={
              isDisabled ||
              (formName === FORM_NAME.UPDATE_ARTICLE &&
                article?.isLinked) || isPublic
            }
          >
            限定公開
          </AntCheckbox>
        </div>

        <Field label='カテゴリー' name='categoryId' required>
          <Select
            placeholder='カテゴリー'
            style={{ width: '100%' }}
            options={categoryOptions}
            filterOption={(input, option) =>
              String(option?.label ?? option?.children ?? '')
                .toLowerCase()
                .includes(input.toLowerCase()) ?? false
            }
            disabled={isDisabled}
          />
        </Field>

        <Field label='タグ' name='tagNames' required>
          <Select
            mode='tags'
            placeholder='タグ'
            style={{ width: '100%' }}
            options={tagOptions}
            filterOption={(input, option) =>
              String(option?.label ?? option?.children ?? '')
                .toLowerCase()
                .includes(input.toLowerCase()) ?? false
            }
            disabled={isDisabled}
          />
        </Field>

        <Field label='本文' name='content' required>
          <Input placeholder='本文' disabled={isDisabled} />
        </Field>

        <Field label='掲載オファー' name='offerIds'>
          <Select
            mode='multiple'
            placeholder='掲載オファー'
            style={{ width: '100%' }}
            options={offerOptions}
            loading={loadingOffers}
            onPopupScroll={(e) => {
              const target = e.target as HTMLDivElement;
              if (
                target.scrollTop + target.offsetHeight >=
                target.scrollHeight - 32 &&
                hasMoreOffers &&
                !loadingOffers
              ) {
                setOfferPage((p) => p + 1);
              }
            }}
            filterOption={(input, option) =>
              String(option?.label ?? option?.children ?? '')
                .toLowerCase()
                .includes(input.toLowerCase()) ?? false
            }
            disabled={isDisabled}
          />
        </Field>

        <Field label='掲載イベント' name='eventIds'>
          <Select
            mode='multiple'
            labelInValue
            placeholder='掲載イベント'
            style={{ width: '100%' }}
            options={eventOptions}
            loading={loadingEvents}
            onPopupScroll={(e) => {
              const target = e.target as HTMLDivElement;
              if (
                target.scrollTop + target.offsetHeight >=
                target.scrollHeight - 32 &&
                hasMoreEvents &&
                !loadingEvents
              ) {
                setEventPage((p) => p + 1);
              }
            }}
            filterOption={(input, option) =>
              String(option?.label ?? option?.children ?? '')
                .toLowerCase()
                .includes(input.toLowerCase()) ?? false
            }
            disabled={isDisabled}
          />
        </Field>

        <label className='title-calendar font-semibold'>
          カバー画像
          <Badge status='error' className='ml-2 mb-1' />
        </label>
        <div className='flex items-baseline gap-2 pt-2'>
          {!coverImage ? (
            <div className='border-solid rounded border-2 border-zinc-300 w-[200px] h-[100px] flex justify-center items-center'>
              <Upload
                beforeUpload={(file: File) => beforeUpload(file)}
                customRequest={() => { }}
                listType='text'
                maxCount={1}
                showUploadList={false}
              >
                <Button
                  type='default'
                  shape='default'
                  size='middle'
                  className='text-[#84ab42] border border-[#84ab42] font-semibold'
                  disabled={isDisabled}
                >
                  画像を選択
                </Button>
              </Upload>
            </div>
          ) : (
            <div className='relative inline-block'>
              <Image
                height={100}
                src={coverImage?.previewUrl}
                className='block rounded-lg'
                fallback={FALLBACK_URL}
              />
              <Button
                type='primary'
                shape='circle'
                icon={<CloseOutlined />}
                size='small'
                onClick={() => setValue('coverImage', null)}
                className={`absolute -top-2 -right-2 z-10 bg-red-500 border-none ${isDisabled && 'hidden'}`}
              />
            </div>
          )}
        </div>
        <label className='title-calendar font-medium text-xs'>
          <span className='font-bold mr-1'>※</span>
          1.8:1(~375×211 px)の写真（正方形）をアップロードしてください
        </label>
        <Field name='attachment' className='mt-[-34px]'>
          <></>
        </Field>

        <div className='flex items-baseline gap-2'>
          <div className='flex-1'>
            <span className='title-calendar font-semibold'>
              公開日時
              <Badge status='error' className='ml-2' />
            </span>
          </div>
          <div className='flex-1'>
            <span className='title-calendar font-semibold ml-2'>
              公開終了日時
            </span>
          </div>
        </div>

        <div className='flex items-baseline gap-1 pt-2'>
          <Field name='publicAt' className='flex-1'>
            <DatePicker
              disabledDate={disabledDate}
              disabledTime={disabledTime}
              showTime
              onCalendarChange={(e) => {
                setValue('publicAt', e ? (e as Moment).second(0) : undefined);
                trigger('publicAt');
                trigger('publicEndAt');
              }}
              disabled={isDisabled || (isPublic && status !== Status.DRAFT)}
            />
          </Field>
          <span>～</span>
          <Field name='publicEndAt' className='flex-1'>
            <DatePicker
              disabledDate={disabledDate}
              disabledTime={disabledTime}
              showTime
              onCalendarChange={(e) => {
                setValue(
                  'publicEndAt',
                  e ? (e as Moment).second(0) : undefined,
                );
                trigger('publicEndAt');
              }}
              disabled={isDisabled}
            />
          </Field>
        </div>

        <Field label='公開ステータス' name='status' className='w-full'>
          <RadioGroup options={isPublic ? statusOptions.map(o => o.value === Status.PUBLIC ? { ...o, label: '公開中' } : o) : statusOptions} disabled={isDisabled} />
        </Field>

        <div className='mb-4'>
          <AntCheckbox
            checked={watch('alwaysShowOnTimeline') ?? false}
            onChange={(e) => setValue('alwaysShowOnTimeline', e.target.checked)}
            disabled={isDisabled}
          >
            タイムラインに常に表示
          </AntCheckbox>
        </div>
      </Spin>
    </Form>
  );
};
